/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { useParams } from "react-router-dom";
import {
  Box,
  Col,
  FlexContainer,
  Message,
  Row,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  CloudProviderType,
  EstateV2Filter,
  SearchSortField,
  SearchSortOrder,
} from "~/generated/graphql";
import {
  ErrorWrap,
  ListLoader,
  LoaderWrap,
  Pagination,
  PaginationState,
  ReactTable,
  SearchBoxContainer,
} from "~/components";
import {
  CommonListProps,
  ESTATE_COLUMNS,
  EstateData,
  mapEstateRecord,
  MetricsResponse,
  ResourcesList,
} from "~/components/EstateRecordsList";
import {
  KUBERNETES_METRICS_COLUMNS,
  PROVIDER_ICON_COLUMN,
} from "~/components/Tables/Columns";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateFilters,
  useEstateRecords,
  useEstateSort,
  useFilteredQueryState,
  useScrollAfterPaging,
} from "~/hooks";
import { useCustomers } from "~/services/customers";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty, noop } from "~/tools";
import { HYBRID_PROVIDERS } from "~/views/accounts/AddAccount/constants";
import {
  EstateActiveFilters,
  EstateFiltersToggle,
  EstateSort,
} from "~/views/estate/components";
import { convertTags, generateRowData } from "~/views/estate/helpers";

type RouteParams = {
  accountId: string;
};

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type EstateQueryState = EstateV2Filter &
  PaginationState &
  SortingProp & {
    tags: string[];
    metadata: string[];
    query: string;
  };

type Props = {
  providerId: string;
  providerType: string;
};

type KubernetesListProps = {
  hasMetricsResources: boolean;
  areMetricsEnabled: boolean;
  metrics: MetricsResponse | undefined;
  metricColumns: unknown;
  metricDetails: unknown;
  showMetricsPagination: boolean;
  metricsCount: number;
  metadata: CommonListProps;
};

export function EstateRecords({ providerId, providerType }: Props) {
  const { isAdminLite } = useCustomers();
  const scrollToRef = useRef<HTMLDivElement>(null);

  const areMetricsEnabled = enableMetrics(providerType);

  const { currency } = useCurrency();

  const { accountId = "" } = useParams<RouteParams>();

  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page,
    limit,
    query,
    field,
    order,
    tags,
    metadata,
    ...rawFilters
  }: EstateQueryState = state;

  const [searchInput, setSearchInput] = useState(query);

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSort({ field, order, updateQueryState });

  const filters = {
    ...rawFilters,
    tags: convertTags(tags),
    metadata: convertTags(metadata),
  };

  const getAccount = () =>
    providerType === CloudProviderType.Kubernetes ? accountId : providerId;

  const { data, loading, error } = useEstateRecords(
    {
      variables: {
        page,
        limit,
        sort: {
          field: sortField,
          order: sortDirection,
        },
        filter: {
          ...filters,
          account: [getAccount()],
        },
        query,
      },
      skip: isEmpty(getAccount()),
    },
    showError
  );

  const {
    data: metrics,
    loading: areMetricsLoading,
    error: loadingError,
  } = useEstateRecords(
    {
      variables: {
        limit,
        page,
        sort: {
          field: sortField,
          order: sortDirection,
        },
        filter: {
          ...filters,
          account: [providerId],
        },
        query,
      },
      skip: isEmpty(providerId) && !areMetricsEnabled,
    },
    noop
  );

  const rows = useMemo(
    () =>
      data?.resources?.map((record) => mapEstateRecord(record, currency)) ?? [],
    [data, currency]
  );

  const metricDetails = useMemo(() => {
    const metricResults = metrics?.resources ?? [];

    return metricResults.map((res) => ({
      ...generateRowData(res, currency),
    }));
  }, [metrics, currency]);

  const columns = useMemo(() => {
    const excludedColumns = isAdminLite ? ["application"] : [];

    return ESTATE_COLUMNS.filter(
      (column) => !excludedColumns.includes(column.accessor)
    );
  }, [isAdminLite]);
  const metricColumns = useMemo(
    () => [PROVIDER_ICON_COLUMN, ...KUBERNETES_METRICS_COLUMNS],
    []
  );

  const hasResources = isNotEmpty(rows);
  const hasMetricsResources = isNotEmpty(metricDetails);

  const estateCount = data?.count ?? 0;
  const metricsCount = metrics?.count ?? 0;
  const showPagination = estateCount > 0;
  const showMetricsPagination = metricsCount > 0;

  const { activeFilters, removeFields, clearFilters, removeFilterValue } =
    useEstateFilters(filters);
  const showFilters = isNotEmpty(activeFilters);

  useScrollAfterPaging({
    scrollToRef,
    enabled: !loading || !areMetricsLoading,
  });

  const commonListProps: CommonListProps = {
    hasResources,
    data,
    columns,
    rows,
    showPagination,
    estateCount,
  };

  return (
    <div ref={scrollToRef}>
      <ErrorWrap
        error={error || loadingError}
        status={
          hasResources || hasMetricsResources
            ? ERROR_COLORS.notification
            : ERROR_COLORS.danger
        }
      >
        <Message
          status="notification"
          image="info"
          width="100%"
          css={{
            marginBottom: theme.spacing.spacing04,
          }}
        >
          <FlexContainer direction="column" alignItems="flex-start">
            <Text
              weight="medium"
              color={theme.color.text.info}
              mb={theme.spacing.spacing02}
            >
              The list includes only Cloud Estate Records.
            </Text>
            <Text mb={theme.spacing.spacing00} color={theme.color.text.text02}>
              The difference between the selected account’s monthly cost and the
              sum of estate records costs is that we don’t include “networking,”
              “discount,” “support,” and “other” costs. Instead, those costs are
              grouped into the additional cost resources.
            </Text>
          </FlexContainer>
        </Message>
        <Box boxStyle="lightGrey" spacing="spacing04">
          <FlexContainer justifyContent="space-between" alignItems="center">
            <EstateSort
              sortField={sortField}
              sortDirection={sortDirection}
              onSortSelect={onSortSelect}
              onSortOrderChange={onSortOrderChange}
            />
            <div
              css={{
                margin: `0 ${theme.spacing.spacing05}`,
                flexGrow: 1,
              }}
            >
              <SearchBoxContainer
                searchInput={searchInput}
                setSearchInput={setSearchInput}
              />
            </div>
            <EstateFiltersToggle
              isAccount
              allowSavingFilters={false}
              onApply={() => {
                setSearchInput(query ?? "");
              }}
            />
          </FlexContainer>
          <When condition={showFilters}>
            <Row>
              <Col md="12">
                <div
                  css={{
                    marginTop: theme.spacing.spacing04,
                    marginLeft: theme.spacing.spacing02,
                  }}
                >
                  <EstateActiveFilters
                    filters={activeFilters}
                    allowSavingFilters={false}
                    onRemoveKey={removeFields}
                    onClear={clearFilters}
                    onRemoveValue={(itemName, index) =>
                      removeFilterValue(itemName as keyof EstateV2Filter, index)
                    }
                  />
                </div>
              </Col>
            </Row>
          </When>
        </Box>
        <LoaderWrap
          isBackground
          loading={loading || areMetricsLoading}
          Component={ListLoader}
        >
          <If condition={areMetricsEnabled}>
            <Then>
              <ResourcesListKubernetes
                hasMetricsResources={hasMetricsResources}
                metrics={metrics}
                areMetricsEnabled={areMetricsEnabled}
                metricColumns={metricColumns}
                metricDetails={metricDetails}
                showMetricsPagination={showMetricsPagination}
                metricsCount={metricsCount}
                metadata={commonListProps}
              />
            </Then>
            <Else>
              <ResourcesList metadata={commonListProps} />
            </Else>
          </If>
        </LoaderWrap>
      </ErrorWrap>
    </div>
  );
}

function ResourcesListKubernetes({
  hasMetricsResources,
  areMetricsEnabled,
  metrics,
  metricColumns,
  metricDetails,
  showMetricsPagination,
  metricsCount,
  metadata,
}: KubernetesListProps) {
  return (
    <If condition={hasMetricsResources && areMetricsEnabled}>
      <Then>
        <div css={{ marginBottom: theme.spacing.spacing04 }}>
          <EstateData
            currentMonthTotal={metrics?.cost?.currentMonthTotal}
            count={metrics?.count}
          />
        </div>
        <ReactTable
          striped
          columns={metricColumns}
          data={metricDetails}
          sort={false}
        />
        {showMetricsPagination && <Pagination count={metricsCount} />}
      </Then>
      <Else>
        <ResourcesList metadata={metadata} />
      </Else>
    </If>
  );
}

function enableMetrics(providerType: string) {
  return (
    providerType === HYBRID_PROVIDERS.openShift ||
    providerType === HYBRID_PROVIDERS.kubernetes
  );
}
